import React, { useCallback, useEffect, useState } from 'react';
import { ConfigProvider, Form, AutoComplete, Input, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { maxWinSize } from 'settings/style';
import {
    ContactDropdownGlobalStyle,
    ContactModalShell,
    ContactOptionMeta,
    ContactOptionName,
    ContactOptionRow,
    ContactOptionText,
    ContactStyledForm,
    contactModalLightTheme,
    LoginBody,
    LoginFooter,
    LoginHeading,
    LoginSubheading,
    SignInButton,
    SignUpLink,
} from './contactModal.styled';

type UserItem = {
    id: string;
    username?: string;
    firstname?: string;
    lastname?: string;
    avatarUrl?: string;
};

const formatUserDisplayName = (user: Pick<UserItem, 'firstname' | 'lastname'>) =>
    [user.firstname, user.lastname].filter(Boolean).join(' ').trim();

interface ContactModalProps {
    open: boolean;
    onCancel: () => void;
    onConfirm: (values: { userId: string; username: string }) => Promise<void> | void;
    loading?: boolean;
    users: UserItem[];
    currentUserId?: string;
    labelView?: string;
}

const COPY = {
    chatview: {
        title: 'Add New Contact',
        subtitle: 'Search by username or name to start a new conversation.',
        fieldLabel: 'Username or Name',
        placeholder: 'Type username or name',
        submit: 'Confirm',
    },
    familytree: {
        title: 'Request to share family tree',
        subtitle: 'Find a user by username to send your sharing request.',
        fieldLabel: 'Username',
        placeholder: 'Type username',
        submit: 'Send request',
    },
} as const;

const ContactModal: React.FC<ContactModalProps> = ({
    open,
    onCancel,
    onConfirm,
    loading = false,
    users,
    currentUserId,
    labelView = 'chatview',
}) => {
    const [form] = Form.useForm();
    const [options, setOptions] = useState<{ value: string; label: React.ReactNode; userId: string }[]>([]);
    const [timer, setTimer] = useState<number | null>(null);

    const copy = labelView === 'familytree' ? COPY.familytree : COPY.chatview;

    const buildOptions = useCallback(
        (q: string) => {
            const ql = q.trim().toLowerCase();
            const opts = (users || [])
                .filter((u) => u.id !== currentUserId)
                .filter((u) => {
                    if (!ql) return true;
                    const displayName = formatUserDisplayName(u);
                    const haystack = [u.username, displayName].filter(Boolean).join(' ').toLowerCase();
                    return haystack.includes(ql);
                })
                .slice(0, 8)
                .map((u) => {
                    const src = (u.avatarUrl || '').trim();
                    const displayName = formatUserDisplayName(u);
                    return {
                        value: u.username || '',
                        userId: u.id,
                        label: (
                            <ContactOptionRow>
                                <Avatar size={24} src={src || undefined} icon={!src ? <UserOutlined /> : undefined} />
                                <ContactOptionText>
                                    <ContactOptionName>{u.username}</ContactOptionName>
                                    {displayName ? <ContactOptionMeta>{displayName}</ContactOptionMeta> : null}
                                </ContactOptionText>
                            </ContactOptionRow>
                        ),
                    };
                });
            setOptions(opts);
        },
        [users, currentUserId],
    );

    useEffect(() => {
        if (!open) return;
        form.resetFields();
        setOptions([]);
    }, [open, form]);

    return (
        <>
            <ContactDropdownGlobalStyle />
            <ConfigProvider theme={contactModalLightTheme}>
                <ContactModalShell
                    open={open}
                    onCancel={onCancel}
                    footer={null}
                    centered
                    destroyOnClose
                    width={440}
                    style={maxWinSize('md') ? { margin: '10px 0px' } : undefined}
                    getContainer={() => document.getElementById('root') || document.body}
                    maskClosable
                >
                    <LoginBody>
                        <LoginHeading>{copy.title}</LoginHeading>
                        <LoginSubheading>{copy.subtitle}</LoginSubheading>
                    </LoginBody>

                    <ContactStyledForm
                        layout="vertical"
                        form={form}
                        preserve={false}
                        onFinish={async () => {
                            const typed = (form.getFieldValue('username') || '').trim();
                            const match = options.find(
                                (opt) => typeof opt.value === 'string' && opt.value.trim() === typed,
                            );
                            if (!match) {
                                form.setFields([{ name: 'username', errors: ['Please select a user from the list'] }]);
                                return;
                            }
                            await onConfirm({ userId: match.userId, username: typed });
                        }}
                        validateTrigger="onChange"
                    >
                        <Form.Item
                            label={copy.fieldLabel}
                            name="username"
                            rules={[{ required: true, message: 'Please pick a user' }]}
                        >
                            <AutoComplete
                                className="contact-autocomplete"
                                popupClassName="contact-modal-autocomplete-dropdown"
                                options={options}
                                placeholder={copy.placeholder}
                                filterOption={false}
                                onSearch={(text) => {
                                    if (timer) window.clearTimeout(timer);
                                    const t = window.setTimeout(() => buildOptions(text), 150);
                                    setTimer(t);
                                }}
                                onFocus={() => buildOptions('')}
                                onSelect={(value, option: any) => {
                                    form.setFieldsValue({ userId: option.userId, username: value });
                                    form.setFields([{ name: 'username', errors: [] }]);
                                }}
                                onChange={(text) => {
                                    form.setFieldsValue({ username: text, userId: undefined });
                                }}
                            >
                                <Input className="auth-input-text" />
                            </AutoComplete>
                        </Form.Item>

                        <Form.Item name="userId" hidden>
                            <Input />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0 }}>
                            <SignInButton htmlType="submit" loading={loading}>
                                {copy.submit}
                            </SignInButton>
                        </Form.Item>
                    </ContactStyledForm>

                    <LoginFooter>
                        <SignUpLink type="button" onClick={onCancel}>
                            Cancel
                        </SignUpLink>
                    </LoginFooter>
                </ContactModalShell>
            </ConfigProvider>
        </>
    );
};

export default ContactModal;
